import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  Users,
  Workflow,
  Key,
  Database,
  Settings,
  Activity,
  Lock,
  Globe,
  PlusCircle,
  RefreshCcw,
  Search,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  UploadCloud,
  FileText,
  Trash2,
  Bell,
  Cpu,
  Zap,
  TrendingUp,
  Package,
  Layers,
  Download,
  Briefcase,
  Cable,
  Radio,
  Edit3,
  Save,
  X,
  Brain,
  DollarSign,
  Eye,
  EyeOff,
  CalendarClock,
  Inbox,
} from 'lucide-react';
import { useRole } from '../hooks/useRole';
import { useAuth } from '../contexts/AuthContext';
import N8nWorkflowManagementScreen from './N8nWorkflowManagementScreen';
import LicenseManagementScreen from './admin/LicenseManagementScreen';
import AbfCopilotSettingsCard from './admin/AbfCopilotSettingsCard';
import BatchJobsScreen from './admin/BatchJobsScreen';
import WebsiteEnquiriesScreen from './admin/WebsiteEnquiriesScreen';
import { cn } from '../lib/utils';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const ContentContainer = styled.div`
  width: 100%;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = 
  ({ label, value, icon, color }) => (
    <motion.div 
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

const HubTab: React.FC<{ 
  icon: React.ReactNode; 
  label: string; 
  active: boolean; 
  onClick: () => void;
}> = ({ icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      "group flex items-center gap-2.5 px-6 py-3 text-sm font-semibold transition-all duration-200 relative",
      active 
        ? "text-white" 
        : "text-slate-400 hover:text-slate-200"
    )}
  >
    <span className={cn(
      "transition-transform duration-200 group-hover:scale-110",
      active ? "text-blue-400" : "text-slate-500 group-hover:text-slate-300"
    )}>
      {icon}
    </span>
    {label}
    {active && (
      <motion.div 
        layoutId="admin-nav-indicator"
        className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]"
      />
    )}
  </button>
);

// ============== Interfaces ==============
interface HealthData {
  overallStatus: string;
  dataStats: {
    users: number;
    portfolios: number;
    securities: number;
    positions: number;
    trades: number;
  };
  openIssues: {
    openReconciliationBreaks: number;
    investigatingBreaks: number;
    totalIssues: number;
    requiresAttention: boolean;
  };
  system: {
    uptime: string;
    memory: {
      used: string;
      total: string;
    };
  };
}

interface ImportStatus {
  type: 'securities' | 'positions' | 'portfolios';
  status: 'success' | 'error' | 'loading';
  message: string;
}

type TabType = 'users' | 'workflows' | 'licensing' | 'settings' | 'sso' | 'import' | 'health' | 'tenants' | 'fix' | 'llm' | 'jobs' | 'enquiries';

interface SsoConfig {
  configured: boolean;
  providerType: string;
  providerName: string;
  clientId: string;
  clientSecretSet: boolean;
  issuerUrl: string;
  scopes: string;
  defaultRole: string;
  autoProvisionUsers: boolean;
  updateUserOnLogin: boolean;
  allowPasswordLogin: boolean;
  enforceSso: boolean;
  isEnabled: boolean;
}

interface FixSession {
  id: number;
  tenantId: number;
  tenantName?: string;
  venueId: number;
  venueName: string;
  venueCode: string;
  sessionName: string;
  senderCompId: string;
  targetCompId: string;
  host: string;
  port: number;
  enabled: boolean;
  connectionStatus?: string;
  lastConnectedAt?: string;
}

interface VenueTenantConfig {
  id: number;
  tenantId: number;
  venueCode: string;
  venueName: string;
  senderCompId: string;
  targetCompId?: string;
  enabled: boolean;
  onboardingStatus: string;
  lastConnectionTime?: string;
}

interface LLMConfig {
  id: number;
  tenantId: number;
  provider: string;
  providerName: string;
  displayName?: string;
  hasCredentials: boolean;
  apiKeyMasked?: string;
  apiEndpoint?: string;
  organizationId?: string;
  projectId?: string;
  region?: string;
  defaultModel?: string;
  rateLimitRpm?: number;
  rateLimitTpm?: number;
  maxTokensPerRequest?: number;
  enabled: boolean;
  isDefault: boolean;
  allowTraining: boolean;
  totalRequests: number;
  totalTokensUsed: number;
  estimatedCostUsd: number;
  lastUsedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface LLMModelDetail {
  id: string;
  label: string;
  tools: boolean;
  caching: boolean;
  temperature: boolean;
}

interface LLMProvider {
  code: string;
  name: string;
  description: string;
  defaultModels: string[];
  /** Bedrock only: labels and verified capabilities for the model picker. */
  modelDetails?: LLMModelDetail[];
}

interface LLMTestResult {
  success: boolean;
  message: string;
}

/** A limit field's value: a positive whole number, or undefined for "no limit". */
const positiveOrUndefined = (raw: string): number | undefined => {
  const value = parseInt(raw, 10);
  return Number.isFinite(value) && value > 0 ? value : undefined;
};

/** Regions offered for Bedrock; the backend also validates the code. */
const BEDROCK_REGIONS = ['us-east-1', 'us-east-2', 'us-west-2', 'eu-west-1', 'eu-central-1', 'ap-southeast-2'];

type PcFundingControlPolicy =
  | 'REQUIRED'
  | 'REQUIRED_FOR_INITIAL_DRAW'
  | 'REQUIRED_FOR_DDTL_AND_REVOLVER'
  | 'WARN_ONLY'
  | 'DISABLED';

interface TenantAdminConfig {
  tenantId: number;
  tenantName: string;
  product?: string;
  clientType?: string;
  tier?: string;
  primaryCustodian?: string | null;
  pcFundingControlPolicy?: PcFundingControlPolicy;
  abfFourEyesPolicy?: 'REQUIRED' | 'OPTIONAL' | 'DISABLED';
}

const pcFundingPolicyOptions: Array<{
  value: PcFundingControlPolicy;
  label: string;
  description: string;
}> = [
  {
    value: 'REQUIRED',
    label: 'Require Approved IC Packet',
    description: 'Every private-credit drawdown requires a current approved packet with all required documents ready.'
  },
  {
    value: 'REQUIRED_FOR_INITIAL_DRAW',
    label: 'Require For First Draw',
    description: 'The first funded draw requires IC approval; later draws under the posted commitment proceed with audit notes.'
  },
  {
    value: 'REQUIRED_FOR_DDTL_AND_REVOLVER',
    label: 'Require For DDTL/Revolver',
    description: 'Delayed-draw and revolver funding require IC approval; standard term-loan draws proceed with audit notes.'
  },
  {
    value: 'WARN_ONLY',
    label: 'Warn And Allow',
    description: 'Drawdowns post but stamp whether an approved IC packet was present.'
  },
  {
    value: 'DISABLED',
    label: 'Disabled',
    description: 'IC packet gate is bypassed; availability, overdraw, compliance disposition, and IBOR posting still apply.'
  }
];

const AdminHub: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, isPlatformAdmin, isTenantAdmin } = useRole();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    // Deep link: /admin?tab=jobs (used by the day-cycle run doc and ops runbooks).
    try {
      const requested = new URLSearchParams(window.location.search).get('tab');
      const known: TabType[] = ['users', 'workflows', 'licensing', 'settings', 'sso', 'import', 'health', 'tenants', 'fix', 'llm', 'jobs'];
      return requested && (known as string[]).includes(requested) ? (requested as TabType) : 'users';
    } catch {
      return 'users';
    }
  });
  const [users, setUsers] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [health, setHealth] = useState<HealthData | null>(null);
  const [importStatuses, setImportStatuses] = useState<ImportStatus[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Add User/Tenant modal state
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddTenantModal, setShowAddTenantModal] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', fullName: '', email: '', role: 'USER', password: '' });
  const [newTenant, setNewTenant] = useState({ tenantName: '', tenantCode: '', clientType: 'RETAIL', clientSubtype: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Edit/View modal state
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showEditTenantModal, setShowEditTenantModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedTenant, setSelectedTenant] = useState<any>(null);

  // SSO Configuration state
  const [ssoTenantId, setSsoTenantId] = useState<string>('');
  const [ssoConfig, setSsoConfig] = useState<Partial<SsoConfig>>({
    providerType: 'OIDC',
    providerName: '',
    clientId: '',
    issuerUrl: '',
    scopes: 'openid profile email',
    defaultRole: 'USER',
    autoProvisionUsers: true,
    updateUserOnLogin: true,
    allowPasswordLogin: true,
    enforceSso: false,
    isEnabled: false,
  });
  const [clientSecret, setClientSecret] = useState('');
  const [savingSso, setSavingSso] = useState(false);

  // FIX Connectivity state
  const [fixSessions, setFixSessions] = useState<FixSession[]>([]);
  const [venueConfigs, setVenueConfigs] = useState<VenueTenantConfig[]>([]);
  const [fixTenantId, setFixTenantId] = useState<string>('');
  const [editingSession, setEditingSession] = useState<FixSession | null>(null);
  const [savingFix, setSavingFix] = useState(false);
  const [sessionHealth, setSessionHealth] = useState<Record<number, { status: string; latency?: number }>>({});

  // LLM Configuration state
  const [llmConfigs, setLlmConfigs] = useState<LLMConfig[]>([]);
  const [llmProviders, setLlmProviders] = useState<LLMProvider[]>([]);
  const [llmTenantId, setLlmTenantId] = useState<string>('');
  const [editingLlmConfig, setEditingLlmConfig] = useState<LLMConfig | null>(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [savingLlm, setSavingLlm] = useState(false);
  const [llmTest, setLlmTest] = useState<LLMTestResult | null>(null);
  const [testingLlm, setTestingLlm] = useState(false);
  const [llmUsage, setLlmUsage] = useState<{ totalTokens: number; totalCostUsd: number } | null>(null);

  // Tenant policy settings
  const [settingsTenantId, setSettingsTenantId] = useState<string>('');
  const [tenantSettings, setTenantSettings] = useState<TenantAdminConfig | null>(null);
  const [loadingTenantSettings, setLoadingTenantSettings] = useState(false);
  const [savingTenantSettings, setSavingTenantSettings] = useState(false);

  const fetchSsoConfig = useCallback(async (tenantId: string) => {
    if (!tenantId) return;
    try {
      const response = await axios.get('/api/admin/sso/config', { params: { tenantId } });
      if (response.data.configured) {
        setSsoConfig(response.data);
      } else {
        setSsoConfig({
          providerType: 'OIDC',
          providerName: '',
          clientId: '',
          issuerUrl: '',
          scopes: 'openid profile email',
          defaultRole: 'USER',
          autoProvisionUsers: true,
          updateUserOnLogin: true,
          allowPasswordLogin: true,
          enforceSso: false,
          isEnabled: false,
          configured: false,
        });
      }
      setClientSecret('');
    } catch (err) {
      console.error('Failed to load SSO config:', err);
    }
  }, []);

  const fetchFixSessions = useCallback(async (tenantId?: string) => {
    try {
      const params = tenantId ? { tenantId } : {};
      const [sessionsRes, healthRes] = await Promise.all([
        axios.get('/api/admin/fix/sessions', { params }),
        axios.get('/api/fix/monitoring/health', { params })
      ]);

      setFixSessions(Array.isArray(sessionsRes.data) ? sessionsRes.data : []);

      // Map health data by session ID
      if (healthRes.data?.sessions) {
        const healthMap: Record<number, { status: string; latency?: number }> = {};
        healthRes.data.sessions.forEach((s: any) => {
          healthMap[s.sessionId] = { status: s.connectionStatus, latency: s.avgLatencyMs };
        });
        setSessionHealth(healthMap);
      }
    } catch (err) {
      console.error('Failed to load FIX sessions:', err);
      setFixSessions([]);
    }
  }, []);

  const fetchVenueConfigs = useCallback(async (tenantId: string) => {
    if (!tenantId) return;
    try {
      const response = await axios.get(`/api/admin/venues/tenant/${tenantId}/configs`);
      setVenueConfigs(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load venue configs:', err);
      setVenueConfigs([]);
    }
  }, []);

  // LLM Configuration functions
  const fetchLlmProviders = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/llm/providers');
      setLlmProviders(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load LLM providers:', err);
    }
  }, []);

  const fetchLlmConfigs = useCallback(async (tenantId: string) => {
    if (!tenantId) return;
    try {
      const [configsRes, usageRes] = await Promise.all([
        axios.get(`/api/admin/llm/configs/tenant/${tenantId}`),
        axios.get(`/api/admin/llm/usage/tenant/${tenantId}`)
      ]);
      setLlmConfigs(Array.isArray(configsRes.data) ? configsRes.data : []);
      if (usageRes.data) {
        setLlmUsage({
          totalTokens: usageRes.data.totalTokens || 0,
          totalCostUsd: usageRes.data.totalCostUsd || 0
        });
      }
    } catch (err) {
      console.error('Failed to load LLM configs:', err);
      setLlmConfigs([]);
    }
  }, []);

  const handleSaveLlmConfig = async () => {
    if (!editingLlmConfig) return;
    setSavingLlm(true);
    try {
      await axios.post('/api/admin/llm/configs', {
        tenantId: editingLlmConfig.tenantId,
        provider: editingLlmConfig.provider,
        displayName: editingLlmConfig.displayName,
        apiKey: newApiKey || undefined,
        apiEndpoint: editingLlmConfig.apiEndpoint,
        organizationId: editingLlmConfig.organizationId,
        region: editingLlmConfig.region,
        defaultModel: editingLlmConfig.defaultModel,
        rateLimitRpm: editingLlmConfig.rateLimitRpm,
        rateLimitTpm: editingLlmConfig.rateLimitTpm,
        maxTokensPerRequest: editingLlmConfig.maxTokensPerRequest,
        enabled: editingLlmConfig.enabled,
        isDefault: editingLlmConfig.isDefault,
        allowTraining: editingLlmConfig.allowTraining,
        username: user?.username
      });
      setEditingLlmConfig(null);
      setNewApiKey('');
      setLlmTest(null);
      fetchLlmConfigs(llmTenantId);
    } catch (err) {
      console.error('Failed to save LLM config:', err);
      const reason = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      alert(reason ? `Failed to save LLM configuration: ${reason}` : 'Failed to save LLM configuration');
    } finally {
      setSavingLlm(false);
    }
  };

  /** Runs the backend's check for a saved configuration; for Bedrock this is one real model call. */
  const handleTestLlmConfig = async (configId: number) => {
    setTestingLlm(true);
    setLlmTest(null);
    try {
      const { data } = await axios.post(`/api/admin/llm/configs/${configId}/test`);
      setLlmTest({ success: Boolean(data?.success), message: String(data?.message ?? '') });
    } catch (err) {
      console.error('LLM config test failed:', err);
      setLlmTest({ success: false, message: 'The test request did not reach the server.' });
    } finally {
      setTestingLlm(false);
    }
  };

  const handleToggleLlmConfig = async (configId: number, enable: boolean) => {
    try {
      await axios.post(`/api/admin/llm/configs/${configId}/${enable ? 'enable' : 'disable'}`);
      fetchLlmConfigs(llmTenantId);
    } catch (err) {
      console.error('Failed to toggle LLM config:', err);
    }
  };

  const handleSetDefaultLlmProvider = async (configId: number) => {
    try {
      await axios.post(`/api/admin/llm/configs/${configId}/set-default`);
      fetchLlmConfigs(llmTenantId);
    } catch (err) {
      console.error('Failed to set default provider:', err);
    }
  };

  const handleDeleteLlmConfig = async (configId: number) => {
    if (!window.confirm('Are you sure you want to delete this LLM configuration? This will remove the API key from Secrets Manager.')) return;
    try {
      await axios.delete(`/api/admin/llm/configs/${configId}`);
      fetchLlmConfigs(llmTenantId);
    } catch (err) {
      console.error('Failed to delete LLM config:', err);
    }
  };

  const fetchTenantSettings = useCallback(async (tenantId: string) => {
    if (!tenantId) return;
    setLoadingTenantSettings(true);
    try {
      const response = await axios.get(`/api/tenant-config/${tenantId}`);
      setTenantSettings(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load tenant settings');
    } finally {
      setLoadingTenantSettings(false);
    }
  }, []);

  const handleSaveTenantSettings = async () => {
    if (!settingsTenantId || !tenantSettings) return;
    setSavingTenantSettings(true);
    try {
      const response = await axios.put(`/api/tenant-config/${settingsTenantId}/settings`, {
        pcFundingControlPolicy: tenantSettings.pcFundingControlPolicy || 'REQUIRED',
        abfFourEyesPolicy: tenantSettings.abfFourEyesPolicy || 'REQUIRED'
      });
      setTenantSettings(response.data);
      setSuccessMessage('Tenant funding-control policy saved');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save tenant funding-control policy');
    } finally {
      setSavingTenantSettings(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'sso' && isPlatformAdmin && ssoTenantId) {
      fetchSsoConfig(ssoTenantId);
    }
  }, [activeTab, isPlatformAdmin, ssoTenantId, fetchSsoConfig]);

  useEffect(() => {
    if (activeTab === 'fix') {
      if (isPlatformAdmin) {
        // Platform admin sees all sessions or filtered by tenant
        fetchFixSessions(fixTenantId || undefined);
        if (fixTenantId) {
          fetchVenueConfigs(fixTenantId);
        }
      } else if (isTenantAdmin && user?.tenantId) {
        // Tenant admin sees only their sessions
        fetchFixSessions(String(user.tenantId));
        fetchVenueConfigs(String(user.tenantId));
      }
    }
  }, [activeTab, isPlatformAdmin, isTenantAdmin, fixTenantId, user?.tenantId, fetchFixSessions, fetchVenueConfigs]);

  useEffect(() => {
    if (activeTab !== 'settings') return;

    if (isPlatformAdmin) {
      if (!settingsTenantId) {
        const preferredTenantId = user?.tenantId ? String(user.tenantId) : tenants[0]?.id ? String(tenants[0].id) : '';
        if (preferredTenantId) {
          setSettingsTenantId(preferredTenantId);
        }
        return;
      }
      fetchTenantSettings(settingsTenantId);
      return;
    }

    if (isTenantAdmin && user?.tenantId) {
      const tenantId = String(user.tenantId);
      if (settingsTenantId !== tenantId) {
        setSettingsTenantId(tenantId);
      }
      fetchTenantSettings(tenantId);
    }
  }, [
    activeTab,
    isPlatformAdmin,
    isTenantAdmin,
    settingsTenantId,
    tenants,
    user?.tenantId,
    fetchTenantSettings
  ]);

  // LLM Configuration useEffect
  useEffect(() => {
    if (activeTab === 'llm') {
      fetchLlmProviders();
      if (isPlatformAdmin && llmTenantId) {
        fetchLlmConfigs(llmTenantId);
      } else if (isTenantAdmin && user?.tenantId) {
        setLlmTenantId(String(user.tenantId));
        fetchLlmConfigs(String(user.tenantId));
      }
    }
  }, [activeTab, isPlatformAdmin, isTenantAdmin, llmTenantId, user?.tenantId, fetchLlmProviders, fetchLlmConfigs]);

  const handleSaveSso = async () => {
    if (!ssoTenantId) return;
    setSavingSso(true);
    try {
      await axios.post('/api/admin/sso/config', {
        ...ssoConfig,
        clientSecret: clientSecret || undefined,
        tenantId: parseInt(ssoTenantId)
      });
      setSuccessMessage('SSO configuration saved successfully');
      fetchSsoConfig(ssoTenantId);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save SSO configuration');
    } finally {
      setSavingSso(false);
    }
  };

  const handleSaveFixSession = async () => {
    if (!editingSession) return;
    setSavingFix(true);
    try {
      await axios.put(`/api/admin/fix/sessions/${editingSession.id}`, {
        sessionName: editingSession.sessionName,
        senderCompId: editingSession.senderCompId,
        host: editingSession.host,
        port: editingSession.port,
        enabled: editingSession.enabled
      });
      setSuccessMessage('FIX session updated successfully');
      setEditingSession(null);
      fetchFixSessions(fixTenantId || (isPlatformAdmin ? undefined : String(user?.tenantId)));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update FIX session');
    } finally {
      setSavingFix(false);
    }
  };

  const handleToggleFixSession = async (sessionId: number, currentlyEnabled: boolean) => {
    try {
      const endpoint = currentlyEnabled ? 'disable' : 'enable';
      await axios.post(`/api/admin/fix/sessions/${sessionId}/${endpoint}`);
      setSuccessMessage(`FIX session ${currentlyEnabled ? 'disabled' : 'enabled'} successfully`);
      fetchFixSessions(fixTenantId || (isPlatformAdmin ? undefined : String(user?.tenantId)));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to toggle FIX session');
    }
  };

  const getConnectionStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'CONNECTED': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
      case 'DISCONNECTED': return 'text-slate-400 bg-slate-500/10 border-slate-500/20';
      case 'CONNECTING':
      case 'RECONNECTING': return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
      case 'ERROR': return 'text-red-400 bg-red-500/10 border-red-500/20';
      default: return 'text-slate-400 bg-slate-500/10 border-slate-500/20';
    }
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const promises = [axios.get('/api/user-management/users')];
      
      if (isPlatformAdmin) {
        promises.push(axios.get('/api/user-management/tenants'));
        promises.push(axios.get('/api/admin/health'));
      } else {
        promises.push(axios.get('/api/admin/health')); // Tenant specific health
      }

      const results = await Promise.all(promises);
      setUsers(Array.isArray(results[0].data) ? results[0].data : []);
      
      if (isPlatformAdmin) {
        setTenants(Array.isArray(results[1].data) ? results[1].data : []);
        setHealth(results[2].data);
      } else {
        setHealth(results[1].data);
      }
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isPlatformAdmin]);

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [fetchData, isAdmin]);

  const handleFileImport = async (type: 'securities' | 'positions' | 'portfolios', file: File) => {
    setImportStatuses(prev => [
      ...prev.filter(s => s.type !== type),
      { type, status: 'loading', message: `Importing ${file.name}...` }
    ]);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('skipHeader', 'true');
    formData.append('updateExisting', 'true');

    try {
      const response = await axios.post(`/api/import/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const data = response.data;
      setImportStatuses(prev => [
        ...prev.filter(s => s.type !== type),
        { 
          type, 
          status: 'success', 
          message: `✓ Imported ${data.imported || 0}, Updated ${data.updated || 0}` 
        }
      ]);
      fetchData();
    } catch (err: any) {
      setImportStatuses(prev => [
        ...prev.filter(s => s.type !== type),
        { 
          type, 
          status: 'error', 
          message: err.response?.data?.error || 'Import failed' 
        }
      ]);
    }
  };

  const getImportStatus = (type: 'securities' | 'positions' | 'portfolios') => {
    return importStatuses.find(s => s.type === type);
  };

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.fullName || !newUser.password) {
      setError('Please fill in all required fields');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await axios.post('/api/user-management/users', newUser);
      setSuccessMessage('User created successfully');
      setShowAddUserModal(false);
      setNewUser({ username: '', fullName: '', email: '', role: 'USER', password: '' });
      fetchData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateTenant = async () => {
    if (!newTenant.tenantName || !newTenant.tenantCode) {
      setError('Please fill in all required fields');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await axios.post('/api/user-management/tenants', newTenant);
      setSuccessMessage('Tenant created successfully');
      setShowAddTenantModal(false);
      setNewTenant({ tenantName: '', tenantCode: '', clientType: 'RETAIL', clientSubtype: '' });
      fetchData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create tenant');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async (userId: number) => {
    if (!window.confirm('Are you sure you want to reset this user\'s password?')) return;
    setIsSaving(true);
    try {
      const response = await axios.post(`/api/user-management/users/${userId}/reset-password`);
      setSuccessMessage(`Password reset. Temporary password: ${response.data.temporaryPassword}`);
      setTimeout(() => setSuccessMessage(''), 10000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleUserStatus = async (userId: number, currentlyActive: boolean) => {
    setIsSaving(true);
    try {
      const endpoint = currentlyActive ? 'deactivate' : 'activate';
      await axios.post(`/api/user-management/users/${userId}/${endpoint}`);
      setSuccessMessage(`User ${currentlyActive ? 'deactivated' : 'activated'} successfully`);
      fetchData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user status');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditUser = (user: any) => {
    setSelectedUser({ ...user });
    setShowEditUserModal(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    setIsSaving(true);
    try {
      await axios.put(`/api/user-management/users/${selectedUser.id}`, {
        fullName: selectedUser.fullName,
        email: selectedUser.email,
        role: selectedUser.role
      });
      setSuccessMessage('User updated successfully');
      setShowEditUserModal(false);
      setSelectedUser(null);
      fetchData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditTenant = (tenant: any) => {
    setSelectedTenant({ ...tenant });
    setShowEditTenantModal(true);
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <GlassCard className="p-12 text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400">This control center is restricted to Administrators only.</p>
        </GlassCard>
      </div>
    );
  }

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <Shield className="w-8 h-8 text-blue-500" />
                {isPlatformAdmin ? 'Platform Command Center' : 'Tenant Admin Hub'}
                <span className={cn(
                  "ml-2 px-3 py-1 border rounded-full text-xs font-bold uppercase tracking-widest",
                  isPlatformAdmin 
                    ? "bg-blue-500/10 border-blue-500/20 text-blue-400" 
                    : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                )}>
                  {isPlatformAdmin ? 'Platform Admin' : 'Tenant Admin'}
                </span>
              </h1>
              <p className="text-slate-500 font-medium mt-1">
                {isPlatformAdmin 
                  ? 'Platform-wide management of users, enterprise licensing, and automated workflows' 
                  : `Management of ${user?.tenantName || 'Tenant'} users, workflows, and configurations`}
              </p>
            </div>
          </div>

          <div className="flex items-center border-b border-white/5 overflow-x-auto custom-scrollbar">
            <HubTab icon={<Users className="w-4 h-4" />} label="Users" active={activeTab === 'users'} onClick={() => setActiveTab('users')} />
            {isPlatformAdmin && <HubTab icon={<Globe className="w-4 h-4" />} label="Tenants" active={activeTab === 'tenants'} onClick={() => setActiveTab('tenants')} />}
            <HubTab icon={<UploadCloud className="w-4 h-4" />} label="Data & Import" active={activeTab === 'import'} onClick={() => setActiveTab('import')} />
            <HubTab icon={<Workflow className="w-4 h-4" />} label="Workflow Engine" active={activeTab === 'workflows'} onClick={() => setActiveTab('workflows')} />
            {isPlatformAdmin && (
              <>
                <HubTab icon={<Key className="w-4 h-4" />} label="License Master" active={activeTab === 'licensing'} onClick={() => setActiveTab('licensing')} />
                <HubTab icon={<Lock className="w-4 h-4" />} label="SSO Config" active={activeTab === 'sso'} onClick={() => setActiveTab('sso')} />
                <HubTab icon={<Inbox className="w-4 h-4" />} label="Enquiries" active={activeTab === 'enquiries'} onClick={() => setActiveTab('enquiries')} />
              </>
            )}
            <HubTab icon={<Activity className="w-4 h-4" />} label="System Health" active={activeTab === 'health'} onClick={() => setActiveTab('health')} />
            <HubTab icon={<CalendarClock className="w-4 h-4" />} label="Batch Jobs" active={activeTab === 'jobs'} onClick={() => setActiveTab('jobs')} />
            <HubTab icon={<Cable className="w-4 h-4" />} label="FIX Connectivity" active={activeTab === 'fix'} onClick={() => setActiveTab('fix')} />
            <HubTab icon={<Brain className="w-4 h-4" />} label="LLM Configuration" active={activeTab === 'llm'} onClick={() => setActiveTab('llm')} />
            <HubTab icon={<Settings className="w-4 h-4" />} label="Global Settings" active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} />
          </div>
        </motion.div>

        <ContentContainer>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === 'users' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <StatCard label="Total Users" value={String(health?.dataStats?.users || users.length)} icon={<Users className="w-5 h-5" />} color="#3b82f6" />
                    <StatCard label="Active Now" value="4" icon={<Activity className="w-5 h-5" />} color="#10b981" />
                    <StatCard label="System Status" value={health?.overallStatus || 'HEALTHY'} icon={<CheckCircle2 className="w-5 h-5" />} color="#8b5cf6" />
                  </div>
                  
                  <GlassCard className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-400" />
                        {isPlatformAdmin ? 'Platform Users' : 'Tenant Users'}
                      </h3>
                      <button
                        onClick={() => setShowAddUserModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-xl text-xs font-bold text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500 transition-all uppercase tracking-widest"
                      >
                        <PlusCircle className="w-4 h-4" /> Add User
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                            <th className="pb-3 px-4">Full Name</th>
                            <th className="pb-3 px-4">Username</th>
                            <th className="pb-3 px-4">Role</th>
                            <th className="pb-3 px-4">Status</th>
                            <th className="pb-3 px-4 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="text-sm">
                          {users.map((u, i) => (
                            <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                              <td className="py-4 px-4 font-bold text-white">{u.fullName}</td>
                              <td className="py-4 px-4 font-mono text-slate-400">{u.username}</td>
                              <td className="py-4 px-4">
                                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                                  {u.role}
                                </span>
                              </td>
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-2 text-emerald-400">
                                  <div className={cn("w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]", !u.isActive && "bg-slate-600 shadow-none")} />
                                  <span className="text-xs font-medium">{u.isActive !== false ? 'Active' : 'Inactive'}</span>
                                </div>
                              </td>
                              <td className="py-4 px-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => handleResetPassword(u.id)}
                                    className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-amber-400 transition-all border border-white/5"
                                    title="Reset Password"
                                  >
                                    <Key className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handleToggleUserStatus(u.id, u.isActive !== false)}
                                    className={cn(
                                      "p-1.5 rounded-lg bg-slate-800 transition-all border border-white/5",
                                      u.isActive !== false ? "text-slate-400 hover:text-red-400" : "text-slate-400 hover:text-emerald-400"
                                    )}
                                    title={u.isActive !== false ? "Deactivate User" : "Activate User"}
                                  >
                                    {u.isActive !== false ? <Lock className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                  </button>
                                  <button
                                    onClick={() => handleEditUser(u)}
                                    className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-all border border-white/5"
                                    title="Edit User"
                                  >
                                    <ChevronRight className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </GlassCard>
                </div>
              )}

              {activeTab === 'tenants' && isPlatformAdmin && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <StatCard label="Total Tenants" value={String(tenants.length)} icon={<Globe className="w-5 h-5" />} color="#3b82f6" />
                    <StatCard label="Active Tenants" value={String(tenants.filter(t => t.active).length)} icon={<CheckCircle2 className="w-5 h-5" />} color="#10b981" />
                    <StatCard label="License Revenue" value="$42.5k" icon={<TrendingUp className="w-5 h-5" />} color="#8b5cf6" />
                  </div>

                  <GlassCard className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Globe className="w-5 h-5 text-blue-400" />
                        Platform Tenants
                      </h3>
                      <button
                        onClick={() => setShowAddTenantModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-xl text-xs font-bold text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500 transition-all uppercase tracking-widest"
                      >
                        <PlusCircle className="w-4 h-4" /> Add Tenant
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                            <th className="pb-3 px-4">Tenant Name</th>
                            <th className="pb-3 px-4">Code</th>
                            <th className="pb-3 px-4">Users</th>
                            <th className="pb-3 px-4">Status</th>
                            <th className="pb-3 px-4 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="text-sm">
                          {tenants.map((t, i) => (
                            <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                              <td className="py-4 px-4 font-bold text-white">{t.tenantName}</td>
                              <td className="py-4 px-4 font-mono text-slate-400">{t.tenantCode}</td>
                              <td className="py-4 px-4 text-slate-300 font-medium">{t.userCount || 0}</td>
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-2 text-emerald-400">
                                  <div className={cn("w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]", !t.active && "bg-slate-600 shadow-none")} />
                                  <span className="text-xs font-medium">{t.active ? 'Active' : 'Inactive'}</span>
                                </div>
                              </td>
                              <td className="py-4 px-4 text-right">
                                <button
                                  onClick={() => handleEditTenant(t)}
                                  className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-all border border-white/5"
                                  title="Tenant Settings"
                                >
                                  <Settings className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </GlassCard>
                </div>
              )}

              {activeTab === 'import' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard label="Total Portfolios" value={String(health?.dataStats?.portfolios || 0)} icon={<Briefcase className="w-5 h-5" />} color="#3b82f6" />
                    <StatCard label="Security Master" value={String(health?.dataStats?.securities || 0)} icon={<Database className="w-5 h-5" />} color="#8b5cf6" />
                    <StatCard label="Total Positions" value={String(health?.dataStats?.positions || 0)} icon={<Layers className="w-5 h-5" />} color="#10b981" />
                    <StatCard label="Total Trades" value={String(health?.dataStats?.trades || 0)} icon={<Activity className="w-5 h-5" />} color="#f59e0b" />
                  </div>

                  <GlassCard className="p-8">
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <UploadCloud className="w-6 h-6 text-blue-400" />
                        Institutional Data Import
                      </h3>
                      <p className="text-slate-500 text-sm mt-1">Bulk import core entities using CSV or Excel templates</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      {['securities', 'positions', 'portfolios'].map((type) => {
                        const status = getImportStatus(type as any);
                        return (
                          <div key={type} className="p-6 bg-slate-950/50 border border-white/10 rounded-2xl flex flex-col items-center text-center space-y-4 group hover:border-blue-500/30 transition-all">
                            <div className="w-16 h-16 rounded-2xl bg-slate-900 border border-white/5 flex items-center justify-center text-slate-400 group-hover:text-blue-400 transition-colors">
                              {type === 'securities' ? <Database className="w-8 h-8" /> : 
                               type === 'positions' ? <Layers className="w-8 h-8" /> : 
                               <Briefcase className="w-8 h-8" />}
                            </div>
                            <div>
                              <h4 className="font-bold text-white capitalize">{type}</h4>
                              <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">Bulk {type} upload</p>
                            </div>
                            
                            <label className="w-full">
                              <input 
                                type="file" 
                                className="hidden" 
                                accept=".csv,.xlsx" 
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleFileImport(type as any, file);
                                  e.target.value = '';
                                }}
                              />
                              <div className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl cursor-pointer transition-all shadow-lg shadow-blue-600/10">
                                Choose File
                              </div>
                            </label>

                            {status && (
                              <div className={cn(
                                "w-full py-2 px-3 rounded-lg text-[10px] font-bold",
                                status.status === 'success' ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" :
                                status.status === 'error' ? "bg-red-500/10 text-red-400 border border-red-500/20" :
                                "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                              )}>
                                {status.status === 'loading' ? <RefreshCcw className="w-3 h-3 animate-spin mx-auto" /> : status.message}
                              </div>
                            )}

                            <div className="text-[10px] text-slate-600 text-left w-full space-y-1">
                              <strong>Required fields:</strong>
                              {type === 'securities' ? <div>cusip, security_name</div> :
                               type === 'positions' ? <div>portfolio_code, cusip, quantity</div> :
                               <div>portfolio_code, portfolio_name</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </GlassCard>
                </div>
              )}

              {activeTab === 'health' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard label="Overall Health" value={health?.overallStatus || 'HEALTHY'} icon={<Activity className="w-5 h-5" />} color="#10b981" />
                    <StatCard label="System Uptime" value={health?.system?.uptime || '99.9%'} icon={<RefreshCcw className="w-5 h-5" />} color="#3b82f6" />
                    <StatCard label="Memory Usage" value={health?.system?.memory?.used || '1.2 GB'} icon={<Cpu className="w-5 h-5" />} color="#8b5cf6" />
                    <StatCard label="Open Issues" value={String(health?.openIssues?.totalIssues || 0)} icon={<AlertCircle className="w-5 h-5" />} color={health?.openIssues?.requiresAttention ? "#ef4444" : "#10b981"} />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <GlassCard className="p-6">
                      <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <Bell className="w-5 h-5 text-amber-400" />
                        System Alerts
                      </h3>
                      <div className="space-y-4">
                        {health?.openIssues?.openReconciliationBreaks ? (
                          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-start gap-4">
                            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center shrink-0">
                              <AlertCircle className="w-6 h-6 text-red-500" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-bold text-white text-sm">Reconciliation Discrepancy</h4>
                              <p className="text-xs text-slate-400 mt-1">{health.openIssues.openReconciliationBreaks} open breaks detected between IBOR and custodian feeds.</p>
                              <button onClick={() => navigate('/reconciliation')} className="text-[10px] font-bold text-red-400 uppercase tracking-widest mt-3 hover:text-red-300 transition-colors">Resolve Breaks →</button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center gap-4">
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            <span className="text-sm text-emerald-400 font-bold">No outstanding reconciliation breaks</span>
                          </div>
                        )}

                        <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-center gap-4">
                          <CheckCircle2 className="w-5 h-5 text-blue-500" />
                          <span className="text-sm text-blue-400 font-bold">Database cluster synchronized</span>
                        </div>
                        
                        <div className="p-4 bg-slate-900 border border-white/5 rounded-2xl flex items-center gap-4 opacity-50">
                          <Activity className="w-5 h-5 text-slate-500" />
                          <span className="text-sm text-slate-500 font-bold">Last backup: 4 hours ago</span>
                        </div>
                      </div>
                    </GlassCard>

                    <GlassCard className="p-6">
                      <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-red-400" />
                        Compliance Overview
                      </h3>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center p-4 bg-red-500/5 rounded-2xl border border-red-500/10">
                          <div className="flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-400" />
                            <div>
                              <div className="text-sm font-bold text-white">Rule Violations</div>
                              <div className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Active Hard Breaches</div>
                            </div>
                          </div>
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-sm font-bold rounded-md border border-red-500/20">2</span>
                        </div>

                        <div className="flex justify-between items-center p-4 bg-amber-500/5 rounded-2xl border border-amber-500/10">
                          <div className="flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-400" />
                            <div>
                              <div className="text-sm font-bold text-white">Soft Warnings</div>
                              <div className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Guideline Exceptions</div>
                            </div>
                          </div>
                          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-sm font-bold rounded-md border border-amber-500/20">5</span>
                        </div>

                        <div className="flex justify-between items-center p-4 bg-emerald-500/5 rounded-2xl border border-emerald-500/10 opacity-50">
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                            <div>
                              <div className="text-sm font-bold text-white">Last Full Sweep</div>
                              <div className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Completed 12m ago</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </GlassCard>

                    <GlassCard className="p-6">
                      <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <Activity className="w-5 h-5 text-blue-400" />
                        Infrastructure
                      </h3>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center p-4 bg-slate-950/50 rounded-2xl border border-white/5">
                          <div className="flex items-center gap-3">
                            <Database className="w-5 h-5 text-slate-400" />
                            <div>
                              <div className="text-sm font-bold text-white">PostgreSQL Cluster</div>
                              <div className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">v15.4 • AWS RDS</div>
                            </div>
                          </div>
                          <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-[10px] font-bold rounded-md border border-emerald-500/20 uppercase">Online</span>
                        </div>

                        <div className="flex justify-between items-center p-4 bg-slate-950/50 rounded-2xl border border-white/5">
                          <div className="flex items-center gap-3">
                            <Zap className="w-5 h-5 text-slate-400" />
                            <div>
                              <div className="text-sm font-bold text-white">Redis Cache</div>
                              <div className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Low Latency • 4.2ms</div>
                            </div>
                          </div>
                          <button className="px-3 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-[10px] font-bold rounded-md border border-red-500/20 uppercase transition-all">Clear Cache</button>
                        </div>

                        <div className="flex justify-between items-center p-4 bg-slate-950/50 rounded-2xl border border-white/5">
                          <div className="flex items-center gap-3">
                            <Shield className="w-5 h-5 text-slate-400" />
                            <div>
                              <div className="text-sm font-bold text-white">Auth Engine</div>
                              <div className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">JWT • 256-bit encryption</div>
                            </div>
                          </div>
                          <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-[10px] font-bold rounded-md border border-emerald-500/20 uppercase">Secure</span>
                        </div>
                      </div>
                    </GlassCard>
                  </div>
                </div>
              )}
              {activeTab === 'workflows' && <N8nWorkflowManagementScreen />}
              {activeTab === 'licensing' && <LicenseManagementScreen />}
              {activeTab === 'jobs' && <BatchJobsScreen />}
              {activeTab === 'enquiries' && isPlatformAdmin && <WebsiteEnquiriesScreen />}
              {activeTab === 'sso' && isPlatformAdmin && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <StatCard label="SSO Active" value={tenants.filter(t => t.active).length > 0 ? "Enabled" : "Disabled"} icon={<Lock className="w-5 h-5" />} color="#3b82f6" />
                    <StatCard label="Identity Providers" value="OIDC" icon={<Shield className="w-5 h-5" />} color="#10b981" />
                    <StatCard label="Auth Security" value="JWT/256" icon={<CheckCircle2 className="w-5 h-5" />} color="#8b5cf6" />
                  </div>

                  <GlassCard className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                      <div className="space-y-6">
                        <div>
                          <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <Lock className="w-6 h-6 text-blue-400" />
                            Identity Federation
                          </h3>
                          <p className="text-slate-500 text-sm mt-1">Configure OIDC / SAML providers for enterprise single sign-on</p>
                        </div>

                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Select Tenant</label>
                            <select 
                              value={ssoTenantId}
                              onChange={e => setSsoTenantId(e.target.value)}
                              className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                            >
                              <option value="">Select Tenant...</option>
                              {tenants.map(t => <option key={t.id} value={t.id}>{t.tenantName}</option>)}
                            </select>
                          </div>

                          <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Provider Type</label>
                            <select 
                              value={ssoConfig.providerType || 'OIDC'}
                              onChange={e => setSsoConfig({...ssoConfig, providerType: e.target.value})}
                              className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                            >
                              <option value="OIDC">OpenID Connect (OIDC)</option>
                              <option value="SAML">SAML 2.0</option>
                            </select>
                          </div>

                          <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Issuer URL</label>
                            <input 
                              value={ssoConfig.issuerUrl || ''}
                              onChange={e => setSsoConfig({...ssoConfig, issuerUrl: e.target.value})}
                              className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all"
                              placeholder="https://auth.provider.com/realms/master"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Client ID</label>
                              <input 
                                value={ssoConfig.clientId || ''}
                                onChange={e => setSsoConfig({...ssoConfig, clientId: e.target.value})}
                                className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all"
                                placeholder="client-id-123"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Client Secret</label>
                              <input 
                                type="password"
                                value={clientSecret}
                                onChange={e => setClientSecret(e.target.value)}
                                className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all"
                                placeholder={ssoConfig.clientSecretSet ? "••••••••••••" : "Enter secret"}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-8 bg-slate-950/30 p-8 rounded-3xl border border-white/5">
                        <div className="space-y-4">
                          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">Security Policies</h4>
                          
                          <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-2xl border border-white/5">
                            <div>
                              <div className="text-sm font-bold text-white">Auto-provisioning</div>
                              <div className="text-[10px] text-slate-500 font-medium">Create user record on first login</div>
                            </div>
                            <input 
                              type="checkbox" 
                              checked={ssoConfig.autoProvisionUsers}
                              onChange={e => setSsoConfig({...ssoConfig, autoProvisionUsers: e.target.checked})}
                              className="w-5 h-5 rounded-lg bg-slate-950 border-white/10 text-blue-600 focus:ring-blue-500/50" 
                            />
                          </div>

                          <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-2xl border border-white/5">
                            <div>
                              <div className="text-sm font-bold text-white">Enforce SSO</div>
                              <div className="text-[10px] text-slate-500 font-medium">Disable password-based login</div>
                            </div>
                            <input 
                              type="checkbox" 
                              checked={ssoConfig.enforceSso}
                              onChange={e => setSsoConfig({...ssoConfig, enforceSso: e.target.checked})}
                              className="w-5 h-5 rounded-lg bg-slate-950 border-white/10 text-blue-600 focus:ring-blue-500/50" 
                            />
                          </div>

                          <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-2xl border border-white/5">
                            <div>
                              <div className="text-sm font-bold text-white">Update on Login</div>
                              <div className="text-[10px] text-slate-500 font-medium">Sync profile attributes every time</div>
                            </div>
                            <input 
                              type="checkbox" 
                              checked={ssoConfig.updateUserOnLogin}
                              onChange={e => setSsoConfig({...ssoConfig, updateUserOnLogin: e.target.checked})}
                              className="w-5 h-5 rounded-lg bg-slate-950 border-white/10 text-blue-600 focus:ring-blue-500/50" 
                            />
                          </div>
                        </div>

                        <div className="pt-4">
                          <button 
                            onClick={handleSaveSso}
                            disabled={savingSso || !ssoTenantId}
                            className="w-full py-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold rounded-2xl transition-all shadow-xl shadow-blue-600/20 flex items-center justify-center gap-3"
                          >
                            {savingSso ? <RefreshCcw className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
                            Save Configuration
                          </button>
                        </div>
                      </div>
                    </div>
                  </GlassCard>
                </div>
              )}
              {activeTab === 'settings' && (
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                  <GlassCard className="p-6 xl:col-span-3">
                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)] gap-8">
                      <div>
                        <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                          <Shield className="w-4 h-4 text-emerald-400" />
                          Private Credit Funding Controls
                        </h3>
                        <p className="text-sm text-slate-400 max-w-3xl">
                          Controls whether a private-credit drawdown must have a current approved IC underwriting packet before creating and posting the drawdown trade.
                        </p>

                        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="p-4 bg-slate-950/40 border border-white/5 rounded-2xl">
                            <div className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Tenant</div>
                            <div className="mt-2 text-sm font-bold text-white">
                              {tenantSettings?.tenantName || (loadingTenantSettings ? 'Loading...' : 'Select tenant')}
                            </div>
                          </div>
                          <div className="p-4 bg-slate-950/40 border border-white/5 rounded-2xl">
                            <div className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Current Policy</div>
                            <div className="mt-2 text-sm font-bold text-white">
                              {tenantSettings?.pcFundingControlPolicy || 'REQUIRED'}
                            </div>
                          </div>
                          <div className="p-4 bg-slate-950/40 border border-white/5 rounded-2xl">
                            <div className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Default</div>
                            <div className="mt-2 text-sm font-bold text-emerald-400">REQUIRED</div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {isPlatformAdmin && (
                          <div className="space-y-2">
                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Tenant</label>
                            <select
                              value={settingsTenantId}
                              onChange={e => {
                                setSettingsTenantId(e.target.value);
                                setTenantSettings(null);
                              }}
                              className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                            >
                              <option value="">Select Tenant...</option>
                              {tenants.map(t => <option key={t.id} value={t.id}>{t.tenantName}</option>)}
                            </select>
                          </div>
                        )}

                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">IC Packet Gate</label>
                          <select
                            value={tenantSettings?.pcFundingControlPolicy || 'REQUIRED'}
                            disabled={!tenantSettings || loadingTenantSettings}
                            onChange={e => setTenantSettings(prev => prev ? {
                              ...prev,
                              pcFundingControlPolicy: e.target.value as PcFundingControlPolicy
                            } : prev)}
                            className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none disabled:opacity-50"
                          >
                            {pcFundingPolicyOptions.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                          <p className="text-xs text-slate-500 leading-relaxed">
                            {pcFundingPolicyOptions.find(option => option.value === (tenantSettings?.pcFundingControlPolicy || 'REQUIRED'))?.description}
                          </p>
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">ABF Four-Eyes Policy</label>
                          <select
                            value={tenantSettings?.abfFourEyesPolicy || 'REQUIRED'}
                            disabled={!tenantSettings || loadingTenantSettings}
                            onChange={e => setTenantSettings(prev => prev ? { ...prev, abfFourEyesPolicy: e.target.value as TenantAdminConfig['abfFourEyesPolicy'] } : prev)}
                            className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none disabled:opacity-50"
                          >
                            <option value="REQUIRED">Required — independent checker</option>
                            <option value="OPTIONAL">Optional — allow same-user completion with evidence</option>
                            <option value="DISABLED">Disabled — no four-eyes gate</option>
                          </select>
                          <p className="text-xs text-slate-500 leading-relaxed">Applies to ABF contract, borrowing-base, funding, covenant, waterfall, and reporting approvals.</p>
                        </div>

                        <button
                          onClick={handleSaveTenantSettings}
                          disabled={savingTenantSettings || loadingTenantSettings || !tenantSettings}
                          className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold rounded-2xl transition-all shadow-xl shadow-blue-600/20 flex items-center justify-center gap-3"
                        >
                          {savingTenantSettings ? <RefreshCcw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                          Save Tenant Setting
                        </button>
                      </div>
                    </div>
                  </GlassCard>

                  <GlassCard className="p-6">
                    <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Globe className="w-4 h-4 text-blue-400" /> Regional Settings</h3>
                    <div className="space-y-4">
                      {['Base Currency', 'Reporting Timezone', 'Date Format'].map(s => (
                        <div key={s} className="flex justify-between items-center py-2 border-b border-white/5">
                          <span className="text-sm text-slate-400 font-medium">{s}</span>
                          <span className="text-sm text-slate-200 font-bold">USD</span>
                        </div>
                      ))}
                    </div>
                  </GlassCard>
                  <GlassCard className="p-6">
                    <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Activity className="w-4 h-4 text-blue-400" /> System Health</h3>
                    <div className="space-y-4">
                      {['API Engine', 'Market Data Feed', 'Database Cluster'].map(s => (
                        <div key={s} className="flex justify-between items-center py-2 border-b border-white/5">
                          <span className="text-sm text-slate-400 font-medium">{s}</span>
                          <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold uppercase">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Healthy
                          </div>
                        </div>
                      ))}
                    </div>
                  </GlassCard>
                </div>
              )}

              {activeTab === 'fix' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <StatCard
                      label="Total Sessions"
                      value={String(fixSessions.length)}
                      icon={<Cable className="w-5 h-5" />}
                      color="#3b82f6"
                    />
                    <StatCard
                      label="Connected"
                      value={String(Object.values(sessionHealth).filter(h => h.status === 'CONNECTED').length)}
                      icon={<Radio className="w-5 h-5" />}
                      color="#10b981"
                    />
                    <StatCard
                      label="Venues"
                      value={String(new Set(fixSessions.map(s => s.venueCode)).size)}
                      icon={<Globe className="w-5 h-5" />}
                      color="#8b5cf6"
                    />
                    <StatCard
                      label="Avg Latency"
                      value={Object.values(sessionHealth).filter(h => h.latency).length > 0
                        ? `${Math.round(Object.values(sessionHealth).reduce((sum, h) => sum + (h.latency || 0), 0) / Object.values(sessionHealth).filter(h => h.latency).length)}ms`
                        : 'N/A'
                      }
                      icon={<Activity className="w-5 h-5" />}
                      color="#f59e0b"
                    />
                  </div>

                  <GlassCard className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                          <Cable className="w-5 h-5 text-blue-400" />
                          FIX Session Configuration
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">Configure session names and SenderCompIDs for each venue</p>
                      </div>
                      {isPlatformAdmin && (
                        <div className="flex items-center gap-3">
                          <select
                            value={fixTenantId}
                            onChange={e => setFixTenantId(e.target.value)}
                            className="bg-slate-950 border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-blue-500/50"
                          >
                            <option value="">All Tenants</option>
                            {tenants.map(t => (
                              <option key={t.id} value={t.id}>{t.tenantName}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => fetchFixSessions(fixTenantId || undefined)}
                            className="p-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-slate-400 hover:text-white transition-all border border-white/5"
                          >
                            <RefreshCcw className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                            <th className="pb-3 px-4">Session Name</th>
                            <th className="pb-3 px-4">Venue</th>
                            <th className="pb-3 px-4">Sender CompID</th>
                            <th className="pb-3 px-4">Target CompID</th>
                            <th className="pb-3 px-4">Host:Port</th>
                            <th className="pb-3 px-4">Status</th>
                            <th className="pb-3 px-4">Enabled</th>
                            <th className="pb-3 px-4 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="text-sm">
                          {fixSessions.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="py-12 text-center text-slate-500">
                                <Cable className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p className="font-medium">No FIX sessions configured</p>
                                <p className="text-xs mt-1">Sessions will appear here once configured for tenants</p>
                              </td>
                            </tr>
                          ) : (
                            fixSessions.map((session) => {
                              const health = sessionHealth[session.id];
                              return (
                                <tr key={session.id} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                                  <td className="py-4 px-4">
                                    <div className="font-bold text-white">{session.sessionName}</div>
                                    {isPlatformAdmin && session.tenantName && (
                                      <div className="text-[10px] text-slate-500 mt-0.5">{session.tenantName}</div>
                                    )}
                                  </td>
                                  <td className="py-4 px-4">
                                    <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                      {session.venueCode}
                                    </span>
                                  </td>
                                  <td className="py-4 px-4 font-mono text-slate-300">{session.senderCompId}</td>
                                  <td className="py-4 px-4 font-mono text-slate-400">{session.targetCompId}</td>
                                  <td className="py-4 px-4 font-mono text-slate-400 text-xs">{session.host}:{session.port}</td>
                                  <td className="py-4 px-4">
                                    <span className={cn(
                                      "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border",
                                      getConnectionStatusColor(health?.status || 'DISCONNECTED')
                                    )}>
                                      {health?.status || 'DISCONNECTED'}
                                    </span>
                                  </td>
                                  <td className="py-4 px-4">
                                    <button
                                      onClick={() => handleToggleFixSession(session.id, session.enabled)}
                                      className={cn(
                                        "w-10 h-5 rounded-full relative transition-colors",
                                        session.enabled ? "bg-emerald-500" : "bg-slate-700"
                                      )}
                                    >
                                      <div className={cn(
                                        "absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-md",
                                        session.enabled ? "right-0.5" : "left-0.5"
                                      )} />
                                    </button>
                                  </td>
                                  <td className="py-4 px-4 text-right">
                                    <button
                                      onClick={() => setEditingSession({ ...session })}
                                      className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-blue-400 transition-all border border-white/5"
                                      title="Edit Session"
                                    >
                                      <Edit3 className="w-3.5 h-3.5" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </GlassCard>

                  {venueConfigs.length > 0 && (
                    <GlassCard className="p-6">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                        <Shield className="w-5 h-5 text-purple-400" />
                        Venue Configurations
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {venueConfigs.map((config) => (
                          <div key={config.id} className="p-4 bg-slate-950/50 border border-white/10 rounded-2xl">
                            <div className="flex items-center justify-between mb-3">
                              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                {config.venueCode}
                              </span>
                              <span className={cn(
                                "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border",
                                config.enabled ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-slate-500/10 text-slate-400 border-slate-500/20"
                              )}>
                                {config.onboardingStatus}
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Sender CompID:</span>
                                <span className="text-white font-mono">{config.senderCompId}</span>
                              </div>
                              {config.lastConnectionTime && (
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Last Connected:</span>
                                  <span className="text-slate-300 text-xs">{new Date(config.lastConnectionTime).toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </GlassCard>
                  )}
                </div>
              )}

              {activeTab === 'llm' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <StatCard
                      label="Total Providers"
                      value={String(llmConfigs.length)}
                      icon={<Brain className="w-5 h-5" />}
                      color="#8b5cf6"
                    />
                    <StatCard
                      label="Active"
                      value={String(llmConfigs.filter(c => c.enabled).length)}
                      icon={<CheckCircle2 className="w-5 h-5" />}
                      color="#10b981"
                    />
                    <StatCard
                      label="Total Tokens"
                      value={llmUsage ? (llmUsage.totalTokens > 1000000 ? `${(llmUsage.totalTokens / 1000000).toFixed(1)}M` : `${Math.round(llmUsage.totalTokens / 1000)}K`) : '0'}
                      icon={<Zap className="w-5 h-5" />}
                      color="#f59e0b"
                    />
                    <StatCard
                      label="Est. Cost"
                      value={llmUsage ? `$${llmUsage.totalCostUsd.toFixed(2)}` : '$0.00'}
                      icon={<DollarSign className="w-5 h-5" />}
                      color="#3b82f6"
                    />
                  </div>

                  <GlassCard className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                          <Brain className="w-5 h-5 text-purple-400" />
                          LLM Provider Configuration
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">Configure API keys and settings for AI providers</p>
                      </div>
                      {isPlatformAdmin && (
                        <div className="flex items-center gap-3">
                          <select
                            value={llmTenantId}
                            onChange={e => {
                              setLlmTenantId(e.target.value);
                              if (e.target.value) fetchLlmConfigs(e.target.value);
                            }}
                            className="bg-slate-950 border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-blue-500/50"
                          >
                            <option value="">Select Tenant</option>
                            {tenants.map(t => (
                              <option key={t.id} value={t.id}>{t.tenantName}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => llmTenantId && fetchLlmConfigs(llmTenantId)}
                            className="p-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-slate-400 hover:text-white transition-all border border-white/5"
                          >
                            <RefreshCcw className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {!llmTenantId && isPlatformAdmin ? (
                      <div className="py-12 text-center text-slate-500">
                        <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="font-medium">Select a tenant to configure LLM providers</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {llmProviders.map(provider => {
                          const config = llmConfigs.find(c => c.provider === provider.code);
                          return (
                            <div
                              key={provider.code}
                              className={cn(
                                "p-4 bg-slate-950/50 border rounded-2xl transition-all",
                                config?.enabled ? "border-purple-500/30" : "border-white/10",
                                config?.isDefault && "ring-2 ring-purple-500/20"
                              )}
                            >
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Brain className="w-5 h-5 text-purple-400" />
                                  <span className="font-bold text-white">{provider.name}</span>
                                </div>
                                {config?.isDefault && (
                                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"
                                    title="Every AI feature of this tenant, the ABF Copilot included, uses this provider and its model">
                                    In use · default
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 mb-4">{provider.description}</p>

                              {config ? (
                                <div className="space-y-3">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">API Key:</span>
                                    <span className={cn(
                                      "font-mono text-xs",
                                      config.hasCredentials ? "text-emerald-400" : "text-amber-400"
                                    )}>
                                      {config.provider === 'BEDROCK' ? `AWS IAM · ${config.region || 'server region'}` : config.hasCredentials ? config.apiKeyMasked || '••••••••' : 'Not Set'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Model:</span>
                                    <span className="text-slate-300 text-xs">{config.defaultModel || 'Default'}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Requests:</span>
                                    <span className="text-slate-300">{config.totalRequests.toLocaleString()}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Tokens:</span>
                                    <span className="text-slate-300">{config.totalTokensUsed.toLocaleString()}</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/10">
                                    <button
                                      onClick={() => {
                                        setEditingLlmConfig(config);
                                        setNewApiKey('');
                                        setShowApiKey(false);
                                      }}
                                      className="flex-1 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs font-medium text-white transition-all flex items-center justify-center gap-1"
                                    >
                                      <Edit3 className="w-3 h-3" /> Edit
                                    </button>
                                    <button
                                      onClick={() => handleToggleLlmConfig(config.id, !config.enabled)}
                                      className={cn(
                                        "px-3 py-2 rounded-lg text-xs font-medium transition-all",
                                        config.enabled
                                          ? "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                                          : "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                                      )}
                                    >
                                      {config.enabled ? 'Disable' : 'Enable'}
                                    </button>
                                    {!config.isDefault && config.enabled && (
                                      <button
                                        onClick={() => handleSetDefaultLlmProvider(config.id)}
                                        className="px-3 py-2 bg-purple-500/10 hover:bg-purple-500/20 rounded-lg text-xs font-medium text-purple-400 transition-all"
                                        title="Make this the tenant's LLM for every AI feature"
                                      >
                                        Make default
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center py-4">
                                  <p className="text-sm text-slate-500 mb-3">Not configured</p>
                                  <button
                                    onClick={() => {
                                      setEditingLlmConfig({
                                        id: 0,
                                        tenantId: parseInt(llmTenantId),
                                        provider: provider.code,
                                        providerName: provider.name,
                                        hasCredentials: false,
                                        enabled: true,
                                        isDefault: llmConfigs.length === 0,
                                        allowTraining: false,
                                        totalRequests: 0,
                                        totalTokensUsed: 0,
                                        estimatedCostUsd: 0,
                                        defaultModel: provider.defaultModels[0]
                                      });
                                      setNewApiKey('');
                                      setShowApiKey(false);
                                    }}
                                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-xs font-medium text-white transition-all flex items-center gap-1 mx-auto"
                                  >
                                    <PlusCircle className="w-3 h-3" /> Configure
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </GlassCard>
                  {llmTenantId && (
                    <AbfCopilotSettingsCard
                      tenantId={llmTenantId}
                      refreshKey={llmConfigs.map(c => `${c.provider}:${c.enabled}:${c.defaultModel ?? ''}`).join('|')}
                    />
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </ContentContainer>
      </div>

      {/* Success/Error Messages */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 bg-emerald-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3"
          >
            <CheckCircle2 className="w-5 h-5" />
            {successMessage}
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 bg-red-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5" />
            {error}
            <button onClick={() => setError(null)} className="ml-2 hover:opacity-70">×</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add User Modal */}
      <AnimatePresence>
        {showAddUserModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowAddUserModal(false)}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-white/10">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  Add New User
                </h2>
                <p className="text-sm text-slate-400 mt-1">Create a new user account</p>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={newUser.fullName}
                    onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Username *</label>
                  <input
                    type="text"
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                    placeholder="jsmith"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                    placeholder="jsmith@company.com"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Role *</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                  >
                    <option value="USER">User</option>
                    <option value="PORTFOLIO_MANAGER">Portfolio Manager</option>
                    <option value="TRADER">Trader</option>
                    <option value="REFERENCE_DATA_MANAGER">Reference Data Manager</option>
                    <option value="COMPLIANCE_OFFICER">Compliance Officer</option>
                    <option value="OPERATIONS_ANALYST">Operations Analyst</option>
                    <option value="MIDDLE_OFFICE_ADMIN">Middle Office Admin</option>
                    <option value="RISK_MANAGER">Risk Manager</option>
                    <option value="RESEARCH_ANALYST">Credit Analyst</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Password *</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex gap-3">
                <button
                  onClick={() => {
                    setShowAddUserModal(false);
                    setNewUser({ username: '', fullName: '', email: '', role: 'USER', password: '' });
                  }}
                  className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateUser}
                  disabled={isSaving}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl text-white font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSaving ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
                  Create User
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add Tenant Modal */}
      <AnimatePresence>
        {showAddTenantModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowAddTenantModal(false)}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-white/10">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-500" />
                  Add New Tenant
                </h2>
                <p className="text-sm text-slate-400 mt-1">Create a new tenant organization</p>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Tenant Name *</label>
                  <input
                    type="text"
                    value={newTenant.tenantName}
                    onChange={(e) => setNewTenant({ ...newTenant, tenantName: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                    placeholder="Acme Capital Partners"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Tenant Code *</label>
                  <input
                    type="text"
                    value={newTenant.tenantCode}
                    onChange={(e) => setNewTenant({ ...newTenant, tenantCode: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white font-mono focus:outline-none focus:border-blue-500/50"
                    placeholder="ACME"
                  />
                  <p className="text-xs text-slate-500 mt-1">Unique identifier (uppercase, no spaces)</p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Client Type *</label>
                  <select
                    value={newTenant.clientType}
                    onChange={(e) => setNewTenant({ ...newTenant, clientType: e.target.value, clientSubtype: '' })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                  >
                    <option value="RETAIL">Retail</option>
                    <option value="INSTITUTIONAL">Institutional</option>
                    <option value="HYBRID">Hybrid</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Client Subtype</label>
                  <select
                    value={newTenant.clientSubtype}
                    onChange={(e) => setNewTenant({ ...newTenant, clientSubtype: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                  >
                    <option value="">Select subtype...</option>
                    {newTenant.clientType === 'RETAIL' && (
                      <>
                        <option value="RIA">RIA (Registered Investment Advisor)</option>
                        <option value="FAMILY_OFFICE">Family Office</option>
                      </>
                    )}
                    {newTenant.clientType === 'INSTITUTIONAL' && (
                      <>
                        <option value="HEDGE_FUND">Hedge Fund</option>
                        <option value="INSURANCE">Insurance Company</option>
                        <option value="ASSET_MANAGER">Asset Manager</option>
                        <option value="PENSION">Pension Fund</option>
                        <option value="ENDOWMENT">Endowment/Foundation</option>
                      </>
                    )}
                    {newTenant.clientType === 'HYBRID' && (
                      <>
                        <option value="RIA">RIA (Registered Investment Advisor)</option>
                        <option value="FAMILY_OFFICE">Family Office</option>
                        <option value="HEDGE_FUND">Hedge Fund</option>
                        <option value="ASSET_MANAGER">Asset Manager</option>
                      </>
                    )}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">Determines available features and modules</p>
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex gap-3">
                <button
                  onClick={() => {
                    setShowAddTenantModal(false);
                    setNewTenant({ tenantName: '', tenantCode: '', clientType: 'RETAIL', clientSubtype: '' });
                  }}
                  className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTenant}
                  disabled={isSaving}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl text-white font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSaving ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
                  Create Tenant
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit User Modal */}
      <AnimatePresence>
        {showEditUserModal && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => { setShowEditUserModal(false); setSelectedUser(null); }}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-white/10">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  Edit User
                </h2>
                <p className="text-sm text-slate-400 mt-1">Update user details</p>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Username</label>
                  <input
                    type="text"
                    value={selectedUser.username}
                    disabled
                    className="w-full px-4 py-3 bg-slate-950/50 border border-white/5 rounded-xl text-slate-500 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={selectedUser.fullName || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, fullName: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Email</label>
                  <input
                    type="email"
                    value={selectedUser.email || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Role</label>
                  <select
                    value={selectedUser.role || 'USER'}
                    onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                  >
                    <option value="USER">User</option>
                    <option value="PORTFOLIO_MANAGER">Portfolio Manager</option>
                    <option value="TRADER">Trader</option>
                    <option value="REFERENCE_DATA_MANAGER">Reference Data Manager</option>
                    <option value="COMPLIANCE_OFFICER">Compliance Officer</option>
                    <option value="OPERATIONS_ANALYST">Operations Analyst</option>
                    <option value="MIDDLE_OFFICE_ADMIN">Middle Office Admin</option>
                    <option value="RISK_MANAGER">Risk Manager</option>
                    <option value="RESEARCH_ANALYST">Credit Analyst</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-950/50 rounded-xl border border-white/5">
                  <div>
                    <div className="text-sm font-bold text-white">Status</div>
                    <div className="text-xs text-slate-500">{selectedUser.isActive !== false ? 'Active' : 'Inactive'}</div>
                  </div>
                  <div className={cn(
                    "w-3 h-3 rounded-full",
                    selectedUser.isActive !== false ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]" : "bg-slate-600"
                  )} />
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex gap-3">
                <button
                  onClick={() => { setShowEditUserModal(false); setSelectedUser(null); }}
                  className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={isSaving}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl text-white font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSaving ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Tenant Modal */}
      <AnimatePresence>
        {showEditTenantModal && selectedTenant && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => { setShowEditTenantModal(false); setSelectedTenant(null); }}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-white/10">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-500" />
                  Tenant Settings
                </h2>
                <p className="text-sm text-slate-400 mt-1">View and manage tenant configuration</p>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Tenant Name</label>
                    <div className="px-4 py-3 bg-slate-950/50 border border-white/5 rounded-xl text-white font-medium">
                      {selectedTenant.tenantName}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Tenant Code</label>
                    <div className="px-4 py-3 bg-slate-950/50 border border-white/5 rounded-xl text-white font-mono">
                      {selectedTenant.tenantCode}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Tenant ID</label>
                    <div className="px-4 py-3 bg-slate-950/50 border border-white/5 rounded-xl text-slate-400 font-mono text-sm">
                      {selectedTenant.id}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Users</label>
                    <div className="px-4 py-3 bg-slate-950/50 border border-white/5 rounded-xl text-white font-medium">
                      {selectedTenant.userCount || 0} users
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-950/50 rounded-xl border border-white/5">
                  <div>
                    <div className="text-sm font-bold text-white">Status</div>
                    <div className="text-xs text-slate-500">{selectedTenant.active ? 'Active' : 'Inactive'}</div>
                  </div>
                  <div className={cn(
                    "w-3 h-3 rounded-full",
                    selectedTenant.active ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]" : "bg-slate-600"
                  )} />
                </div>

                <div className="pt-4 border-t border-white/10">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Quick Actions</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        setShowEditTenantModal(false);
                        setActiveTab('users');
                      }}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white text-sm font-medium transition-all border border-white/5"
                    >
                      <Users className="w-4 h-4" />
                      View Users
                    </button>
                    <button
                      onClick={() => {
                        setShowEditTenantModal(false);
                        setActiveTab('licensing');
                      }}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white text-sm font-medium transition-all border border-white/5"
                    >
                      <Key className="w-4 h-4" />
                      License
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-white/10">
                <button
                  onClick={() => { setShowEditTenantModal(false); setSelectedTenant(null); }}
                  className="w-full px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-semibold transition-all"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit FIX Session Modal */}
      <AnimatePresence>
        {editingSession && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setEditingSession(null)}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="p-6 border-b border-white/10">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Cable className="w-5 h-5 text-blue-500" />
                  Edit FIX Session
                </h2>
                <p className="text-sm text-slate-400 mt-1">Configure session parameters for {editingSession.venueCode}</p>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Venue</label>
                    <div className="px-4 py-3 bg-slate-950/50 border border-white/5 rounded-xl text-slate-400 font-mono">
                      {editingSession.venueCode}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Target CompID</label>
                    <div className="px-4 py-3 bg-slate-950/50 border border-white/5 rounded-xl text-slate-400 font-mono">
                      {editingSession.targetCompId}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Session Name *</label>
                  <input
                    type="text"
                    value={editingSession.sessionName}
                    onChange={(e) => setEditingSession({ ...editingSession, sessionName: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                    placeholder="e.g., T1_TRADEWEB"
                  />
                  <p className="text-xs text-slate-500 mt-1">Unique identifier for this session</p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Sender CompID *</label>
                  <input
                    type="text"
                    value={editingSession.senderCompId}
                    onChange={(e) => setEditingSession({ ...editingSession, senderCompId: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '') })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white font-mono focus:outline-none focus:border-blue-500/50"
                    placeholder="e.g., AXIOM_T1_OMS"
                  />
                  <p className="text-xs text-slate-500 mt-1">Your unique identifier on the FIX network (uppercase, no spaces)</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Host</label>
                    <input
                      type="text"
                      value={editingSession.host}
                      onChange={(e) => setEditingSession({ ...editingSession, host: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white font-mono focus:outline-none focus:border-blue-500/50"
                      placeholder="localhost"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Port</label>
                    <input
                      type="number"
                      value={editingSession.port}
                      onChange={(e) => setEditingSession({ ...editingSession, port: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white font-mono focus:outline-none focus:border-blue-500/50"
                      placeholder="9876"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-950/50 rounded-xl border border-white/5">
                  <div>
                    <div className="text-sm font-bold text-white">Session Enabled</div>
                    <div className="text-xs text-slate-500">Allow connections to this venue</div>
                  </div>
                  <button
                    onClick={() => setEditingSession({ ...editingSession, enabled: !editingSession.enabled })}
                    className={cn(
                      "w-12 h-6 rounded-full relative transition-colors",
                      editingSession.enabled ? "bg-emerald-500" : "bg-slate-700"
                    )}
                  >
                    <div className={cn(
                      "absolute top-1 w-4 h-4 rounded-full bg-white transition-transform shadow-md",
                      editingSession.enabled ? "right-1" : "left-1"
                    )} />
                  </button>
                </div>

                {sessionHealth[editingSession.id] && (
                  <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5">
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Connection Status</div>
                    <div className="flex items-center gap-3">
                      <span className={cn(
                        "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border",
                        getConnectionStatusColor(sessionHealth[editingSession.id]?.status || 'DISCONNECTED')
                      )}>
                        {sessionHealth[editingSession.id]?.status || 'DISCONNECTED'}
                      </span>
                      {sessionHealth[editingSession.id]?.latency && (
                        <span className="text-xs text-slate-400">
                          Latency: {sessionHealth[editingSession.id].latency}ms
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-white/10 flex gap-3">
                <button
                  onClick={() => setEditingSession(null)}
                  className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-semibold transition-all flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSaveFixSession}
                  disabled={savingFix || !editingSession.sessionName || !editingSession.senderCompId}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl text-white font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingFix ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit LLM Config Modal */}
      <AnimatePresence>
        {editingLlmConfig && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setEditingLlmConfig(null)}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-white/10">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-500" />
                  {editingLlmConfig.id ? 'Edit' : 'Configure'} {editingLlmConfig.providerName}
                </h2>
                <p className="text-sm text-slate-400 mt-1">Configure API credentials and settings</p>
              </div>

              <div className="p-6 space-y-4">
                {editingLlmConfig.provider === 'BEDROCK' ? (
                  <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5 text-xs text-slate-400">
                    Bedrock needs no API key. Calls use this server&apos;s AWS identity, in the region below, so prompts stay in your AWS account.
                  </div>
                ) : (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">API Key</label>
                  <div className="relative">
                    <input
                      type={showApiKey ? "text" : "password"}
                      value={newApiKey}
                      onChange={(e) => setNewApiKey(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white font-mono focus:outline-none focus:border-purple-500/50 pr-12"
                      placeholder={editingLlmConfig.hasCredentials ? "Enter new key to update..." : "Enter API key..."}
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                    >
                      {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {editingLlmConfig.hasCredentials && (
                    <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Current key: {editingLlmConfig.apiKeyMasked}
                    </p>
                  )}
                </div>
                )}

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Display Name</label>
                  <input
                    type="text"
                    value={editingLlmConfig.displayName || ''}
                    onChange={(e) => setEditingLlmConfig({ ...editingLlmConfig, displayName: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                    placeholder="e.g., Production OpenAI"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Default Model</label>
                  <select
                    value={editingLlmConfig.defaultModel || ''}
                    onChange={(e) => setEditingLlmConfig({ ...editingLlmConfig, defaultModel: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                  >
                    <option value="">Select Model</option>
                    {(() => {
                      const provider = llmProviders.find(p => p.code === editingLlmConfig.provider);
                      if (provider?.modelDetails?.length) {
                        return provider.modelDetails.map(model => (
                          <option key={model.id} value={model.id}>{model.label}</option>
                        ));
                      }
                      return provider?.defaultModels.map(model => (
                        <option key={model} value={model}>{model}</option>
                      ));
                    })()}
                  </select>
                  {(() => {
                    const detail = llmProviders
                      .find(p => p.code === editingLlmConfig.provider)
                      ?.modelDetails?.find(m => m.id === editingLlmConfig.defaultModel);
                    if (!detail) return null;
                    return (
                      <p className="text-xs text-slate-500 mt-1">
                        {detail.tools ? 'Tool calls supported' : 'No tool calls, so the copilot cannot use it'}
                        {' · '}
                        {detail.caching ? 'Prompt caching supported' : 'No prompt caching'}
                      </p>
                    );
                  })()}
                </div>

                {editingLlmConfig.provider === 'BEDROCK' && (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">AWS Region</label>
                    <select
                      value={editingLlmConfig.region || ''}
                      onChange={(e) => setEditingLlmConfig({ ...editingLlmConfig, region: e.target.value || undefined })}
                      className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                    >
                      <option value="">Server default</option>
                      {BEDROCK_REGIONS.map(region => (
                        <option key={region} value={region}>{region}</option>
                      ))}
                    </select>
                  </div>
                )}

                {(editingLlmConfig.provider === 'OPENAI' || editingLlmConfig.provider === 'AZURE_OPENAI') && (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Organization ID</label>
                    <input
                      type="text"
                      value={editingLlmConfig.organizationId || ''}
                      onChange={(e) => setEditingLlmConfig({ ...editingLlmConfig, organizationId: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white font-mono focus:outline-none focus:border-purple-500/50"
                      placeholder="org-..."
                    />
                  </div>
                )}

                {editingLlmConfig.provider === 'AZURE_OPENAI' && (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">API Endpoint</label>
                    <input
                      type="text"
                      value={editingLlmConfig.apiEndpoint || ''}
                      onChange={(e) => setEditingLlmConfig({ ...editingLlmConfig, apiEndpoint: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white font-mono focus:outline-none focus:border-purple-500/50"
                      placeholder="https://your-resource.openai.azure.com"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Rate Limit (calls / min)</label>
                    <input
                      type="number"
                      min={1}
                      placeholder="No limit"
                      value={editingLlmConfig.rateLimitRpm ?? ''}
                      onChange={(e) => setEditingLlmConfig({ ...editingLlmConfig, rateLimitRpm: positiveOrUndefined(e.target.value) })}
                      className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Max Tokens per call</label>
                    <input
                      type="number"
                      min={1}
                      placeholder="No limit"
                      value={editingLlmConfig.maxTokensPerRequest ?? ''}
                      onChange={(e) => setEditingLlmConfig({ ...editingLlmConfig, maxTokensPerRequest: positiveOrUndefined(e.target.value) })}
                      className="w-full px-4 py-3 bg-slate-950 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 -mt-2">
                  Enforced on every call this tenant makes to this provider. Over the rate limit a call is refused, not sent elsewhere.
                  Max tokens caps each answer; document extraction asks for up to 8,192 and the ABF Copilot up to 3,000. Blank means no limit.
                </p>

                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between p-4 bg-slate-950/50 rounded-xl border border-white/5">
                    <div>
                      <div className="text-sm font-bold text-white">Enabled</div>
                      <div className="text-xs text-slate-500">Allow using this provider</div>
                    </div>
                    <button
                      onClick={() => setEditingLlmConfig({ ...editingLlmConfig, enabled: !editingLlmConfig.enabled })}
                      className={cn(
                        "w-12 h-6 rounded-full relative transition-colors",
                        editingLlmConfig.enabled ? "bg-emerald-500" : "bg-slate-700"
                      )}
                    >
                      <div className={cn(
                        "absolute top-1 w-4 h-4 rounded-full bg-white transition-transform shadow-md",
                        editingLlmConfig.enabled ? "right-1" : "left-1"
                      )} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-slate-950/50 rounded-xl border border-white/5">
                    <div>
                      <div className="text-sm font-bold text-white">Set as Default</div>
                      <div className="text-xs text-slate-500">Every AI feature for this tenant, the ABF Copilot included, uses this provider and its model.</div>
                    </div>
                    <button
                      onClick={() => setEditingLlmConfig({ ...editingLlmConfig, isDefault: !editingLlmConfig.isDefault })}
                      className={cn(
                        "w-12 h-6 rounded-full relative transition-colors",
                        editingLlmConfig.isDefault ? "bg-purple-500" : "bg-slate-700"
                      )}
                    >
                      <div className={cn(
                        "absolute top-1 w-4 h-4 rounded-full bg-white transition-transform shadow-md",
                        editingLlmConfig.isDefault ? "right-1" : "left-1"
                      )} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-amber-500/5 rounded-xl border border-amber-500/20">
                    <div>
                      <div className="text-sm font-bold text-amber-400">Allow Training</div>
                      <div className="text-xs text-slate-500">Allow provider to use data for model training (not recommended)</div>
                    </div>
                    <button
                      onClick={() => setEditingLlmConfig({ ...editingLlmConfig, allowTraining: !editingLlmConfig.allowTraining })}
                      className={cn(
                        "w-12 h-6 rounded-full relative transition-colors",
                        editingLlmConfig.allowTraining ? "bg-amber-500" : "bg-slate-700"
                      )}
                    >
                      <div className={cn(
                        "absolute top-1 w-4 h-4 rounded-full bg-white transition-transform shadow-md",
                        editingLlmConfig.allowTraining ? "right-1" : "left-1"
                      )} />
                    </button>
                  </div>
                </div>

                {editingLlmConfig.id !== 0 && (
                  <div className="pt-4">
                    <button
                      onClick={() => {
                        handleDeleteLlmConfig(editingLlmConfig.id);
                        setEditingLlmConfig(null);
                      }}
                      className="w-full px-4 py-3 bg-red-500/10 hover:bg-red-500/20 rounded-xl text-red-400 font-semibold transition-all flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Configuration
                    </button>
                  </div>
                )}

                {llmTest && (
                  <p
                    role="status"
                    className={cn('text-sm p-3 rounded-xl border', llmTest.success
                      ? 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10'
                      : 'text-red-300 border-red-500/30 bg-red-500/10')}
                  >
                    {llmTest.success ? 'Test passed. ' : 'Test failed. '}{llmTest.message}
                  </p>
                )}
              </div>

              <div className="p-6 border-t border-white/10 flex gap-3">
                {editingLlmConfig.id ? (
                  <button
                    onClick={() => handleTestLlmConfig(editingLlmConfig.id)}
                    disabled={testingLlm}
                    className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {testingLlm ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Test
                  </button>
                ) : null}
                <button
                  onClick={() => {
                    setEditingLlmConfig(null);
                    setNewApiKey('');
                    setShowApiKey(false);
                    setLlmTest(null);
                  }}
                  className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl text-white font-semibold transition-all flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSaveLlmConfig}
                  disabled={savingLlm || (editingLlmConfig.provider !== 'BEDROCK' && !editingLlmConfig.hasCredentials && !newApiKey)}
                  className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-500 rounded-xl text-white font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingLlm ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Configuration
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default AdminHub;
